import { ComponentProps, ReactNode } from 'react';

import { ProfileModel } from '@pukka/api';
import { stringUtils } from '@pukka/common/utils';
import { SelectModal } from '@pukka/ui-components';

import { OutletSelectorModal } from '../OutletSelector';

import { OutletSelectorV2Footer } from './OutletSelectorV2Footer';
import { OutletSelectorV2Header } from './OutletSelectorV2Header';

const { concatOnlyIfDefined } = stringUtils;

interface OutletSelectorV2Props {
  readonly selectedIds?: readonly number[];
  readonly disabledIds?: readonly number[];
  readonly maxItems?: number;
  readonly minItems?: number;
  readonly open?: boolean;
  readonly children?: ReactNode;
  readonly filterBy?: string;
  readonly canSelectAll?: boolean;
  readonly errorMessage?: string | ReactNode;
  readonly onSubmit?: (outlets: Set<ProfileModel.Outlet>) => void;
  readonly onClickCancel?: () => void;
}

interface OutletRendererProps {
  readonly outlet: ProfileModel.Outlet;
}

function OutletRenderer({ outlet }: OutletRendererProps) {
  const { name, address, city, province } = outlet;

  return (
    <SelectModal.Item.Default item={outlet} className="md:w-full">
      <div className="w-full overflow-hidden whitespace-normal font-medium text-neutral-700">
        {name}
        <br />
        <span className="font-normal text-neutral-500">{concatOnlyIfDefined(', ', address, city, province) || 'Address not found'}</span>
      </div>
    </SelectModal.Item.Default>
  );
}

function OutletsRenderer({
  filtered,
  selections,
}: ComponentProps<Exclude<ComponentProps<typeof OutletSelectorModal>['customRenderer'], undefined>>) {
  const selectedOutlets = [...selections];
  const unselectedOutlets = [...filtered].filter((o) => !selectedOutlets.includes(o));

  return (
    <>
      {selectedOutlets.map((outlet) => {
        return <OutletRenderer key={outlet.id} outlet={outlet} />;
      })}
      {unselectedOutlets.map((outlet) => {
        return <OutletRenderer key={outlet.id} outlet={outlet} />;
      })}
    </>
  );
}

function OutletSelectorV2({
  selectedIds,
  disabledIds,
  maxItems,
  minItems = 0,
  open,
  filterBy,
  children,
  canSelectAll,
  errorMessage,
  onSubmit,
  onClickCancel,
}: OutletSelectorV2Props) {
  return (
    <OutletSelectorModal
      title="Choose Outlet"
      buttonTitle="Save"
      minItems={minItems}
      maxItems={maxItems}
      selectedIds={selectedIds}
      disabledIds={disabledIds}
      open={open}
      filterBy={filterBy}
      header={OutletSelectorV2Header}
      footer={OutletSelectorV2Footer}
      customRenderer={OutletsRenderer}
      canSelectAll={canSelectAll}
      errorMessage={errorMessage}
      onSubmit={onSubmit}
      onClickCancel={onClickCancel}
    >
      {children}
    </OutletSelectorModal>
  );
}

export { OutletSelectorV2 as OutletSelectorV2Modal };
